/**
 * Exchange-group product-grouping types + Zod schemas + equality, previously
 * defined in `redo/model/src/team.ts`. Lives in return/core/common so leaf
 * packages can reference them without depending on redo/model.
 *
 * `ExchangeGroupType` / `PriceDifferenceType` are registered via `typedUnion`
 * in `redo/backend/db/schema/src/ExchangeGroup.ts`; keep those in sync.
 */
import * as zExt from "@redotech/rpc/ext";
import type { Equal } from "@redotech/util/equal";
import {
  arrayEqual,
  objectEqual,
  optionalEqual,
  stringEqual,
} from "@redotech/util/equal";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "./draft-return/date-schema";

export interface CollectionInfo {
  id: string;
  name: string;
  kind: "smart" | "custom";
  productsCount?: number;
}

export type ExchangeGroupType =
  | "tag:tag"
  | "tag:collection"
  | "name:collection"
  | "collection:collection";

export type PriceDifferenceType = "ignore" | "apply";

export interface BaseExchangeGroup {
  /** Set when the group is saved on the team; absent on a group built in code. */
  _id?: { toString(): string };
  type: ExchangeGroupType;
  groupName: string;
  priceDifference: PriceDifferenceType;
}

export interface TagForTagGroup extends BaseExchangeGroup {
  type: "tag:tag";
  sourceTags: string[];
  targetTags: string[];
}

export interface NameForCollectionGroup extends BaseExchangeGroup {
  type: "name:collection";
  sourceName: string;
  targetCollection: CollectionInfo;
}

export interface TagForCollectionGroup extends BaseExchangeGroup {
  type: "tag:collection";
  sourceTags: string[];
  targetCollection: CollectionInfo;
}

export interface CollectionForCollectionGroup extends BaseExchangeGroup {
  type: "collection:collection";
  sourceCollection: CollectionInfo;
  targetCollection: CollectionInfo;
}
const CollectionInfoZodSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  kind: createEnum(["smart", "custom"], { enumName: "CollectionKind" }),
});
export const CollectionInfoZodSchema: z.ZodType<CollectionInfo> =
  CollectionInfoZodSchema_;
export const ExchangeGroupType = [
  "tag:tag",
  "tag:collection",
  "name:collection",
  "collection:collection",
] as const;

export const ExchangeGroupTypeZodSchema = createEnum(ExchangeGroupType, {
  enumName: "ExchangeGroupType",
});

export const PriceDifferenceType = ["ignore", "apply"] as const;
export const PriceDifferenceTypeZodSchema = createEnum(PriceDifferenceType, {
  enumName: "PriceDifferenceType",
});
/**
 * An exchange group as it applies to one item: the group's name and whether
 * it charges the price difference. Persisted on the item when it enters a
 * draft, so what the shopper was shown cannot move when the merchant edits
 * groups or the catalog afterwards.
 */
export const ExchangeGroupTermsZodSchema = z.object({
  /** The group's `_id`, or its name for a group saved before groups had ids. */
  groupId: z.string(),
  groupName: z.string(),
  priceDifference: PriceDifferenceTypeZodSchema,
});
export type ExchangeGroupTerms = z.infer<typeof ExchangeGroupTermsZodSchema>;

/**
 * What identifies a group across the two sides of a swap. Names are not
 * unique: a merchant may name every collection's group the same, and a
 * returned item and a purchase must pair only within one group.
 */
export const exchangeGroupId = (group: BaseExchangeGroup): string =>
  group._id?.toString() ?? group.groupName;

/**
 * One product's exchange-group matches, taken when the product entered a
 * draft: the groups whose source criteria it matches, so it may be returned
 * under them, and the groups whose target criteria it matches, so it may be
 * bought under them. Membership is a fact of the product, not the variant.
 */
export const ExchangeGroupMatchZodSchema = z.object({
  productId: z.string(),
  /** A Date in memory; an ISO string once CongoDB has read it back from JSONB. */
  snapshotAt: DateFromDateOrIsoStringSchema,
  sourceOf: z.array(ExchangeGroupTermsZodSchema),
  targetOf: z.array(ExchangeGroupTermsZodSchema),
});
export type ExchangeGroupMatch = z.infer<typeof ExchangeGroupMatchZodSchema>;

/**
 * Inputs the returns-accounting engine froze while the document was built.
 * Absent on a document that predates the engine.
 */
export const ReturnsAccountingZodSchema = z.object({
  exchangeGroupMatchSnapshot: z.array(ExchangeGroupMatchZodSchema).nullish(),
});
export type ReturnsAccounting = z.infer<typeof ReturnsAccountingZodSchema>;

export const BaseExchangeGroupZodSchema = z.object({
  _id: zExt.objectId().optional(),
  type: ExchangeGroupTypeZodSchema,
  groupName: z.string(),
  priceDifference: PriceDifferenceTypeZodSchema,
});

export const TagForTagGroupZodSchema: z.ZodType<TagForTagGroup> =
  BaseExchangeGroupZodSchema.extend({
    type: z.literal("tag:tag"),
    sourceTags: z.array(z.string()),
    targetTags: z.array(z.string()),
  });

export const NameForCollectionGroupZodSchema: z.ZodType<NameForCollectionGroup> =
  BaseExchangeGroupZodSchema.extend({
    type: z.literal("name:collection"),
    sourceName: z.string(),
    targetCollection: CollectionInfoZodSchema_,
  });

export const TagForCollectionGroupZodSchema: z.ZodType<TagForCollectionGroup> =
  BaseExchangeGroupZodSchema.extend({
    type: z.literal("tag:collection"),
    sourceTags: z.array(z.string()),
    targetCollection: CollectionInfoZodSchema_,
  });

export const CollectionForCollectionGroupZodSchema: z.ZodType<CollectionForCollectionGroup> =
  BaseExchangeGroupZodSchema.extend({
    type: z.literal("collection:collection"),
    sourceCollection: CollectionInfoZodSchema_,
    targetCollection: CollectionInfoZodSchema_,
  });

export const ExchangeGroupZodSchema: z.ZodType<ExchangeGroup> = z.union([
  TagForTagGroupZodSchema,
  NameForCollectionGroupZodSchema,
  TagForCollectionGroupZodSchema,
  CollectionForCollectionGroupZodSchema,
]);

// If you update this, make sure to update the ExchangeGroupZodSchema
// Use ExchangeGroupZodSchema over this when possible
export type ExchangeGroup =
  | TagForTagGroup
  | NameForCollectionGroup
  | TagForCollectionGroup
  | CollectionForCollectionGroup;

export const exchangeGroupEqual: Equal<ExchangeGroup> =
  objectEqual<ExchangeGroup>({
    type: stringEqual,
    groupName: stringEqual,
    priceDifference: stringEqual,
    sourceName: optionalEqual(stringEqual),
    sourceTags: optionalEqual(arrayEqual(stringEqual)),
    targetTags: optionalEqual(arrayEqual(stringEqual)),
    sourceCollection: optionalEqual(
      objectEqual<CollectionInfo>({
        id: stringEqual,
        name: stringEqual,
        kind: stringEqual,
      }),
    ),
    targetCollection: optionalEqual(
      objectEqual<CollectionInfo>({
        id: stringEqual,
        name: stringEqual,
        kind: stringEqual,
      }),
    ),
  });
